(function () {
  const TARGET_CLASS = 'kpo-coordinates-enhanced';
  const PICKER_ROOT_SELECTOR = '.kpo-coordinate-picker';
  const SOURCE_HIDDEN_CLASS = 'kpo-coordinate-picker__source-hidden';
  const HIDDEN_CLASS = 'kpo-coordinate-picker__hidden';
  const DEGREE_SYMBOL = '\u00B0';
  const DM_SYMBOL_PATTERN = /^\d{1,3}\u00B0\d{1,2}'[NS]\s+\d{1,3}\u00B0\d{1,2}'[EW]$/i;
  const DM_ASCII_PATTERN = /^\d{1,3}deg\d{1,2}'[NS]\s+\d{1,3}deg\d{1,2}'[EW]$/i;
  const DM_COMPACT_PATTERN = /^(\d{3,5})'([NS])(?:\s*,?\s*)(\d{3,5})'([EW])$/i;
  const SESSION_KEY = 'kpo_coordinate_picker_guard_v1';
  const FORM_BOUND_KEY = 'kpoCoordinateFormBound';
  const FORM_DATA_BOUND_KEY = 'kpoCoordinateFormDataBound';
  const COOLDOWN_REFRESH_MS = 1000;
  const DEFAULTS = {
    googleMapsApiKey: '',
    inputWindowMs: 10 * 60 * 1000,
    inputLimit: 300,
    selectionWindowMs: 2 * 60 * 1000,
    selectionLimit: 25,
    selectionSessionCap: 80,
    cooldownMs: 10 * 60 * 1000,
  };

  const pickerStates = [];
  let mapsLoadPromise = null;
  let mutationObserver = null;
  let scheduledRefresh = false;
  let cooldownTimerId = null;
  let pickerId = 0;

  class SessionSoftGuard {
    constructor(config) {
      this.config = config;
      this.state = this.load();
    }

    load() {
      const fallback = {
        inputEvents: [],
        selectionEvents: [],
        totalSelections: 0,
        cooldownUntil: 0,
      };

      try {
        const raw = window.sessionStorage.getItem(SESSION_KEY);
        if (!raw) return fallback;
        const parsed = JSON.parse(raw);
        return Object.assign(fallback, parsed || {});
      } catch (error) {
        return fallback;
      }
    }

    save() {
      try {
        window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(this.state));
      } catch (error) {
        return;
      }
    }

    prune(now) {
      const inputCutoff = now - this.config.inputWindowMs;
      const selectionCutoff = now - this.config.selectionWindowMs;
      this.state.inputEvents = (this.state.inputEvents || []).filter((timestamp) => timestamp >= inputCutoff);
      this.state.selectionEvents = (this.state.selectionEvents || []).filter((timestamp) => timestamp >= selectionCutoff);
    }

    getCooldownRemainingMs(now = Date.now()) {
      return Math.max(0, (this.state.cooldownUntil || 0) - now);
    }

    startCooldown(now) {
      this.state.cooldownUntil = now + this.config.cooldownMs;
      this.save();
      return {
        allowed: false,
        remainingMs: this.config.cooldownMs,
      };
    }

    registerInput() {
      const now = Date.now();
      this.prune(now);

      const remainingMs = this.getCooldownRemainingMs(now);
      if (remainingMs > 0) {
        return { allowed: false, remainingMs };
      }

      this.state.inputEvents.push(now);
      if (this.state.inputEvents.length > this.config.inputLimit) {
        return this.startCooldown(now);
      }

      this.save();
      return { allowed: true, remainingMs: 0 };
    }

    registerSelection() {
      const now = Date.now();
      this.prune(now);

      const remainingMs = this.getCooldownRemainingMs(now);
      if (remainingMs > 0) {
        return { allowed: false, remainingMs };
      }

      this.state.selectionEvents.push(now);
      this.state.totalSelections = Number(this.state.totalSelections || 0) + 1;

      if (
        this.state.selectionEvents.length > this.config.selectionLimit ||
        this.state.totalSelections > this.config.selectionSessionCap
      ) {
        return this.startCooldown(now);
      }

      this.save();
      return { allowed: true, remainingMs: 0 };
    }
  }

  const config = getConfig();
  const guard = new SessionSoftGuard(config);

  function getConfig() {
    const globalConfig = window.KPOCoordinatePickerConfig || {};
    return Object.assign({}, DEFAULTS, globalConfig);
  }

  function readyForAutocomplete() {
    return Boolean(
      window.google &&
        window.google.maps &&
        window.google.maps.places &&
        window.google.maps.places.Autocomplete
    );
  }

  function loadGoogleMapsPlaces() {
    if (readyForAutocomplete()) return Promise.resolve();
    if (!config.googleMapsApiKey) return Promise.reject(new Error('Google Maps API key is missing.'));
    if (mapsLoadPromise) return mapsLoadPromise;

    mapsLoadPromise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector('script[data-kpo-google-maps-script]');
      if (existingScript) {
        waitForAutocomplete(resolve, reject);
        return;
      }

      const script = document.createElement('script');
      script.async = true;
      script.defer = true;
      script.dataset.kpoGoogleMapsScript = 'true';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
        config.googleMapsApiKey
      )}&libraries=places&v=weekly&loading=async`;
      script.addEventListener('load', () => waitForAutocomplete(resolve, reject), { once: true });
      script.addEventListener(
        'error',
        () => reject(new Error('Could not load Google Maps Places script.')),
        { once: true }
      );
      document.head.appendChild(script);
    }).catch((error) => {
      mapsLoadPromise = null;
      throw error;
    });

    return mapsLoadPromise;
  }

  function waitForAutocomplete(resolve, reject) {
    const start = Date.now();
    const timeoutMs = 15000;
    const pollMs = 120;

    const intervalId = window.setInterval(() => {
      if (readyForAutocomplete()) {
        window.clearInterval(intervalId);
        resolve();
        return;
      }

      if (Date.now() - start > timeoutMs) {
        window.clearInterval(intervalId);
        reject(new Error('Google Maps Places timed out before becoming ready.'));
      }
    }, pollMs);
  }

  function collectTargets(root) {
    const matches = [];
    if (root && root.nodeType === Node.ELEMENT_NODE && root.classList.contains(TARGET_CLASS)) {
      matches.push(root);
    }

    const scopedRoot = root && root.querySelectorAll ? root : document;
    scopedRoot.querySelectorAll(`.${TARGET_CLASS}`).forEach((element) => {
      matches.push(element);
    });

    return matches;
  }

  function isSupportedInput(field) {
    if (!field || field.disabled) return false;
    if (field.tagName === 'TEXTAREA') return true;
    if (field.tagName !== 'INPUT') return false;
    const type = (field.getAttribute('type') || 'text').toLowerCase();
    return type === 'text' || type === 'search';
  }

  function findSourceInput(target) {
    if (!target) return null;
    if (target.matches && target.matches(PICKER_ROOT_SELECTOR)) return null;
    if (isSupportedInput(target)) return target;

    const propertyCandidates = Array.from(
      target.querySelectorAll('input[name^="properties["], textarea[name^="properties["], input[name*="properties["], textarea[name*="properties["]')
    ).filter((field) => {
      if (!isSupportedInput(field)) return false;
      if (field.dataset.kpoCoordinateEnhanced === 'true') return false;
      if (field.closest(PICKER_ROOT_SELECTOR)) return false;
      return true;
    });

    if (propertyCandidates.length > 0) {
      return propertyCandidates[0];
    }

    const descendants = Array.from(target.querySelectorAll('input, textarea')).filter((field) => {
      if (!isSupportedInput(field)) return false;
      if (field.dataset.kpoCoordinateEnhanced === 'true') return false;
      if (field.closest(PICKER_ROOT_SELECTOR)) return false;
      return true;
    });

    return descendants[0] || null;
  }

  function getLabelText(target, sourceInput) {
    if (sourceInput.id) {
      try {
        const byFor = document.querySelector(`label[for="${CSS.escape(sourceInput.id)}"]`);
        if (byFor && byFor.textContent) return byFor.textContent.trim();
      } catch (error) {
        const byForFallback = document.querySelector(`label[for="${sourceInput.id}"]`);
        if (byForFallback && byForFallback.textContent) return byForFallback.textContent.trim();
      }
    }

    const scopedLabel = target.closest('label') || target.querySelector('label');
    if (scopedLabel && scopedLabel.textContent) return scopedLabel.textContent.trim();

    return 'Location';
  }

  function createPickerUi(labelText) {
    pickerId += 1;

    const root = document.createElement('div');
    root.className = 'kpo-coordinate-picker';
    root.dataset.kpoCoordinatePicker = String(pickerId);

    const searchId = `KPOCoordinateSearch-${pickerId}`;

    const searchInput = document.createElement('input');
    searchInput.type = 'text';
    searchInput.id = searchId;
    searchInput.className = 'kpo-coordinate-picker__search';
    searchInput.autocomplete = 'off';
    searchInput.placeholder = 'Start typing an address or place';
    searchInput.spellcheck = false;
    searchInput.setAttribute('aria-label', labelText || 'Location');

    const helperLine = document.createElement('p');
    helperLine.className = 'kpo-coordinate-picker__helper';
    helperLine.textContent = 'Pick a suggestion to confirm the location and coordinates.';

    const addressLine = document.createElement('p');
    addressLine.className = `kpo-coordinate-picker__resolved ${HIDDEN_CLASS}`;
    addressLine.setAttribute('aria-live', 'polite');

    const coordinatesLabel = document.createElement('div');
    coordinatesLabel.className = `ymq-option-title ${HIDDEN_CLASS}`;

    const coordinatesLabelText = document.createElement('span');
    coordinatesLabelText.className = 'ymq_lable ';
    coordinatesLabelText.textContent = 'Coordinates';

    const coordinatesTextSpan = document.createElement('span');
    coordinatesTextSpan.className = 'ymq_option_text_span';

    const coordinatesPriceSpan = document.createElement('span');
    coordinatesPriceSpan.className = 'ymq_option_price_span';

    coordinatesLabel.appendChild(coordinatesLabelText);
    coordinatesLabel.appendChild(coordinatesTextSpan);
    coordinatesLabel.appendChild(coordinatesPriceSpan);

    const coordinatesInput = document.createElement('input');
    coordinatesInput.type = 'text';
    coordinatesInput.readOnly = true;
    coordinatesInput.className = `kpo-coordinate-picker__coordinates-input ${HIDDEN_CLASS}`;
    coordinatesInput.setAttribute('aria-live', 'polite');

    const accuracyLine = document.createElement('p');
    accuracyLine.className = `kpo-coordinate-picker__accuracy ${HIDDEN_CLASS}`;
    accuracyLine.setAttribute('aria-live', 'polite');

    const errorLine = document.createElement('p');
    errorLine.className = `kpo-coordinate-picker__error ${HIDDEN_CLASS}`;
    errorLine.setAttribute('role', 'alert');

    root.appendChild(helperLine);
    root.appendChild(searchInput);
    root.appendChild(addressLine);
    root.appendChild(coordinatesLabel);
    root.appendChild(coordinatesInput);
    root.appendChild(accuracyLine);
    root.appendChild(errorLine);

    return {
      root,
      searchInput,
      helperLine,
      addressLine,
      coordinatesLabel,
      coordinatesInput,
      accuracyLine,
      errorLine,
    };
  }

  function dispatchInputEvents(input) {
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function setSourceValue(state, nextValue) {
    const value = sanitizeSingleLine(nextValue || '');
    if (state.sourceInput.value !== value) {
      state.sourceInput.value = value;
      dispatchInputEvents(state.sourceInput);
    }
    if (state.submitInput) {
      if (state.submitInput.value !== value) {
        state.submitInput.value = value;
        dispatchInputEvents(state.submitInput);
      }
    }
  }

  function buildSubmitInputName(sourceInput, labelText) {
    const sourceName = sanitizeSingleLine(sourceInput.getAttribute('name'));
    if (sourceName && sourceName.startsWith('properties[')) return sourceName;

    const fallbackLabel = sanitizeSingleLine(labelText || 'Coordinates').replace(/[\[\]]+/g, '');
    const propertyLabel = fallbackLabel || 'Coordinates';
    return `properties[${propertyLabel}]`;
  }

  function findProductForm(sourceInput, target) {
    const sourceForm = sourceInput.closest('form[data-type="add-to-cart-form"]');
    if (sourceForm) return sourceForm;

    const sourceProductForm = sourceInput.closest('product-form');
    if (sourceProductForm) {
      const nestedForm = sourceProductForm.querySelector('form');
      if (nestedForm) return nestedForm;
    }

    const targetProductForm = target.closest('product-form');
    if (targetProductForm) {
      const nestedForm = targetProductForm.querySelector('form');
      if (nestedForm) return nestedForm;
    }

    const productInfoRoot = target.closest('product-info') || sourceInput.closest('product-info');
    if (productInfoRoot) {
      const scopedForm = productInfoRoot.querySelector('form[data-type="add-to-cart-form"]');
      if (scopedForm) return scopedForm;
    }

    return document.querySelector('form[data-type="add-to-cart-form"]');
  }

  function findFirstPropertyField(form, excludeField) {
    if (!form) return null;

    const fields = Array.from(
      form.querySelectorAll(
        'input[name^="properties["], textarea[name^="properties["], select[name^="properties["], input[name*="properties["], textarea[name*="properties["], select[name*="properties["]'
      )
    );

    return (
      fields.find((field) => {
        if (field === excludeField) return false;
        if (!field.name) return false;
        if (field.dataset.kpoCoordinateSubmit === 'true') return false;
        return true;
      }) || null
    );
  }

  function placeSubmitInputFirst(form, submitInput) {
    if (!form || !submitInput) return;

    if (submitInput.parentElement !== form) {
      form.appendChild(submitInput);
    }

    const firstPropertyField = findFirstPropertyField(form, submitInput);
    if (firstPropertyField) {
      let anchor = firstPropertyField;
      while (anchor && anchor.parentElement !== form) {
        anchor = anchor.parentElement;
      }

      if (anchor && anchor.parentElement === form) {
        form.insertBefore(submitInput, anchor);
      } else if (form.firstChild !== submitInput) {
        form.insertBefore(submitInput, form.firstChild);
      }
    } else if (form.firstChild !== submitInput) {
      form.insertBefore(submitInput, form.firstChild);
    }
  }

  function showAddress(state, value) {
    state.addressLine.textContent = `Selected address: ${value}`;
    state.addressLine.classList.remove(HIDDEN_CLASS);
  }

  function clearAddress(state) {
    state.addressLine.textContent = '';
    state.addressLine.classList.add(HIDDEN_CLASS);
  }

  function showCoordinates(state, value) {
    state.coordinatesLabel.classList.remove(HIDDEN_CLASS);
    state.coordinatesInput.value = value;
    state.coordinatesInput.classList.remove(HIDDEN_CLASS);
  }

  function clearCoordinates(state) {
    state.coordinatesLabel.classList.add(HIDDEN_CLASS);
    state.coordinatesInput.value = '';
    state.coordinatesInput.classList.add(HIDDEN_CLASS);
  }

  function showAccuracy(state, message) {
    state.accuracyLine.textContent = message;
    state.accuracyLine.classList.remove(HIDDEN_CLASS);
  }

  function clearAccuracy(state) {
    state.accuracyLine.textContent = '';
    state.accuracyLine.classList.add(HIDDEN_CLASS);
  }

  function setError(state, message, type) {
    state.errorType = type || 'validation';
    state.errorLine.textContent = message;
    state.errorLine.classList.remove(HIDDEN_CLASS);
  }

  function clearError(state, type) {
    if (type && state.errorType !== type) return;
    state.errorType = '';
    state.errorLine.textContent = '';
    state.errorLine.classList.add(HIDDEN_CLASS);
  }

  function clearBlurValidationTimer(state) {
    if (!state || !state.blurValidationTimerId) return;
    window.clearTimeout(state.blurValidationTimerId);
    state.blurValidationTimerId = null;
  }

  function formatCooldown(remainingMs) {
    const seconds = Math.ceil(remainingMs / 1000);
    if (seconds <= 60) return `${seconds} second${seconds === 1 ? '' : 's'}`;
    const minutes = Math.ceil(seconds / 60);
    return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  }

  function refreshCooldownState(state) {
    if (state.manualFallback) return;

    const remainingMs = guard.getCooldownRemainingMs();
    if (remainingMs > 0) {
      state.searchInput.disabled = true;
      setError(
        state,
        `Too many rapid location lookups detected. Please wait ${formatCooldown(
          remainingMs
        )} and try again.`,
        'cooldown'
      );
      return;
    }

    if (state.searchInput.disabled) state.searchInput.disabled = false;
    if (state.errorType === 'cooldown') clearError(state, 'cooldown');
  }

  function refreshCooldownStates() {
    pickerStates.forEach((state) => refreshCooldownState(state));
  }

  function ensureCooldownTicker() {
    refreshCooldownStates();
    if (cooldownTimerId) return;

    cooldownTimerId = window.setInterval(() => {
      refreshCooldownStates();
      if (guard.getCooldownRemainingMs() <= 0) {
        window.clearInterval(cooldownTimerId);
        cooldownTimerId = null;
      }
    }, COOLDOWN_REFRESH_MS);
  }

  function invalidateSelection(state, withMessage) {
    state.isValidSelection = false;
    state.lastCommittedQuery = '';
    clearAddress(state);
    clearCoordinates(state);
    clearAccuracy(state);
    setSourceValue(state, '');

    if (withMessage) {
      setError(state, 'Location changed. Pick a suggestion to confirm coordinates.', 'validation');
    }
  }

  function buildResolvedAddress(place, fallbackValue) {
    const placeName = sanitizeSingleLine(place && place.name ? String(place.name).trim() : '');
    const formatted = sanitizeSingleLine(
      place && place.formatted_address ? String(place.formatted_address).trim() : ''
    );

    if (placeName && formatted) {
      const lowerName = placeName.toLowerCase();
      const lowerFormatted = formatted.toLowerCase();
      if (lowerFormatted.startsWith(`${lowerName},`) || lowerFormatted === lowerName) return formatted;
      return `${placeName}, ${formatted}`;
    }

    return formatted || placeName || sanitizeSingleLine(fallbackValue || '');
  }

  function formatDmCoordinate(value, positiveHemisphere, negativeHemisphere, degreeToken) {
    const numeric = Number(value);
    const absolute = Math.abs(numeric);
    let degrees = Math.floor(absolute);
    let minutes = Math.round((absolute - degrees) * 60);
    if (minutes === 60) {
      degrees += 1;
      minutes = 0;
    }
    const hemisphere = numeric >= 0 ? positiveHemisphere : negativeHemisphere;
    return `${degrees}${degreeToken}${String(minutes).padStart(2, '0')}'${hemisphere}`;
  }

  function formatDmCoordinates(lat, lng, degreeToken) {
    const latDm = formatDmCoordinate(lat, 'N', 'S', degreeToken);
    const lngDm = formatDmCoordinate(lng, 'E', 'W', degreeToken);
    return `${latDm} ${lngDm}`;
  }

  function parseCompactDm(value) {
    const match = String(value || '').match(DM_COMPACT_PATTERN);
    if (!match) return '';

    const latDigits = match[1];
    const latHemisphere = String(match[2] || '').toUpperCase();
    const lngDigits = match[3];
    const lngHemisphere = String(match[4] || '').toUpperCase();

    if (latDigits.length <= 2 || lngDigits.length <= 2) return '';

    const latDegreeLength = latDigits.length - 2;
    const lngDegreeLength = lngDigits.length - 2;
    const latDegrees = latDigits.slice(0, latDegreeLength);
    const latMinutes = latDigits.slice(latDegreeLength);
    const lngDegrees = lngDigits.slice(0, lngDegreeLength);
    const lngMinutes = lngDigits.slice(lngDegreeLength);

    return `${latDegrees}${DEGREE_SYMBOL}${latMinutes}'${latHemisphere} ${lngDegrees}${DEGREE_SYMBOL}${lngMinutes}'${lngHemisphere}`;
  }

  function toDisplayDm(value) {
    const cleaned = sanitizeSingleLine(value);
    if (!cleaned) return '';
    if (DM_SYMBOL_PATTERN.test(cleaned)) return cleaned;
    if (DM_ASCII_PATTERN.test(cleaned)) return cleaned.replace(/deg/gi, DEGREE_SYMBOL);

    return parseCompactDm(cleaned);
  }

  function getCommittedCoordinateValue(state) {
    if (state.submitInput && !state.submitInput.disabled) return state.submitInput.value;
    if (state.sourceInput) return state.sourceInput.value;
    return '';
  }

  function hasCommittedCoordinates(state) {
    return Boolean(toDisplayDm(getCommittedCoordinateValue(state)));
  }

  function normalizeCommittedCoordinates(state) {
    const normalized = toDisplayDm(getCommittedCoordinateValue(state));
    if (!normalized) return false;
    setSourceValue(state, normalized);
    return true;
  }

  function isElementVisible(element) {
    if (!element || !element.isConnected) return false;
    if (element.hidden) return false;
    if (element.getAttribute('aria-hidden') === 'true') return false;
    return element.getClientRects().length > 0;
  }

  function clearStateWithoutMessage(state) {
    if (!state) return;
    state.searchInput.value = '';
    invalidateSelection(state, false);
    clearError(state);
  }

  function readLatLng(place) {
    if (!place || !place.geometry || !place.geometry.location) return null;

    const location = place.geometry.location;
    const lat = typeof location.lat === 'function' ? location.lat() : location.lat;
    const lng = typeof location.lng === 'function' ? location.lng() : location.lng;

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng };
  }

  function handlePlaceSelection(state) {
    clearBlurValidationTimer(state);

    const selectionGuard = guard.registerSelection();
    if (!selectionGuard.allowed) {
      ensureCooldownTicker();
      return;
    }

    const place = state.autocomplete.getPlace();
    const latLng = readLatLng(place);
    if (!latLng) {
      invalidateSelection(state, false);
      setError(state, 'Select a location from the autocomplete suggestions.', 'validation');
      return;
    }

    const dmCoordinates = formatDmCoordinates(latLng.lat, latLng.lng, DEGREE_SYMBOL);
    const resolvedAddress = buildResolvedAddress(place, state.searchInput.value);

    state.isValidSelection = true;
    state.lastCommittedQuery = state.searchInput.value.trim();
    showAddress(state, resolvedAddress);
    showCoordinates(state, dmCoordinates);
    showAccuracy(
      state,
      'Coordinates are rounded for perfect fit.'
    );
    clearError(state, 'validation');
    setSourceValue(state, dmCoordinates);
  }

  function enableManualFallback(state, message) {
    state.manualFallback = true;
    state.searchInput.disabled = true;
    state.root.classList.add('kpo-coordinate-picker--fallback');
    setError(state, message, 'fallback');

    state.isValidSelection = false;
    clearAddress(state);
    clearCoordinates(state);
    clearAccuracy(state);
    setSourceValue(state, '');
    if (state.submitInput) state.submitInput.disabled = true;
  }

  function bindAutocomplete(state) {
    loadGoogleMapsPlaces()
      .then(() => {
        if (!state.sourceInput.isConnected || state.manualFallback) return;
        state.autocomplete = new window.google.maps.places.Autocomplete(state.searchInput, {
          fields: ['formatted_address', 'geometry', 'name'],
        });
        state.autocomplete.addListener('place_changed', () => handlePlaceSelection(state));
        refreshCooldownState(state);
      })
      .catch((error) => {
        console.error('KPO coordinate picker:', error);
        enableManualFallback(
          state,
          'Location autocomplete is temporarily unavailable. Coordinates are required, so this item cannot be added right now.'
        );
      });
  }

  function bindSearchEvents(state) {
    state.searchInput.addEventListener('input', () => {
      clearBlurValidationTimer(state);

      if (state.manualFallback) return;

      const inputGuard = guard.registerInput();
      if (!inputGuard.allowed) {
        ensureCooldownTicker();
        return;
      }

      if (state.isValidSelection && state.searchInput.value.trim() !== state.lastCommittedQuery) {
        invalidateSelection(state, true);
        return;
      }

      if (state.errorType === 'validation' && !state.searchInput.value.trim()) {
        clearError(state, 'validation');
      }
    });

    state.searchInput.addEventListener('blur', () => {
      clearBlurValidationTimer(state);
      state.blurValidationTimerId = window.setTimeout(() => {
        state.blurValidationTimerId = null;

        if (state.manualFallback || guard.getCooldownRemainingMs() > 0) return;
        if (!state.searchInput.value.trim()) return;
        if (state.isValidSelection) return;
        setError(state, 'Select a location from the autocomplete suggestions.', 'validation');
      }, 180);
    });
  }

  function getStatesForForm(form) {
    return pickerStates.filter((state) => state.form === form && state.sourceInput.isConnected);
  }

  function reorderFormDataProperties(formData, coordinateEntries) {
    const allEntries = Array.from(formData.entries());
    const propertyEntries = [];

    allEntries.forEach((entry) => {
      const key = String(entry[0] || '');
      if (key.indexOf('properties[') === 0) {
        propertyEntries.push(entry);
      }
    });

    if (propertyEntries.length === 0 && coordinateEntries.size === 0) return;

    const propertyNames = new Set();
    propertyEntries.forEach((entry) => propertyNames.add(String(entry[0] || '')));
    coordinateEntries.forEach((value, key) => {
      if (!value) return;
      propertyNames.add(key);
    });

    propertyNames.forEach((name) => formData.delete(name));

    coordinateEntries.forEach((value, name) => {
      if (!value) return;
      formData.append(name, value);
    });

    propertyEntries.forEach((entry) => {
      const name = String(entry[0] || '');
      if (coordinateEntries.has(name)) return;
      formData.append(name, entry[1]);
    });
  }

  function bindFormValidation(state) {
    const form = state.form || findProductForm(state.sourceInput, state.target);
    if (!form) return;

    state.form = form;
    placeSubmitInputFirst(form, state.submitInput);
    if (form.dataset[FORM_DATA_BOUND_KEY] !== 'true') {
      form.dataset[FORM_DATA_BOUND_KEY] = 'true';
      form.addEventListener('formdata', (event) => {
        const states = getStatesForForm(form);
        const coordinateEntries = new Map();
        states.forEach((entry) => {
          if (entry.manualFallback || !entry.submitInput || !entry.submitInput.name) return;
          const normalized = toDisplayDm(getCommittedCoordinateValue(entry));
          if (!normalized) return;
          coordinateEntries.set(entry.submitInput.name, normalized);
          if (entry.submitInput) entry.submitInput.value = normalized;
          if (entry.sourceInput) entry.sourceInput.value = normalized;
        });
        reorderFormDataProperties(event.formData, coordinateEntries);
      });
    }
    if (form.dataset[FORM_BOUND_KEY] === 'true') return;
    form.dataset[FORM_BOUND_KEY] = 'true';

    form.addEventListener(
      'submit',
      (event) => {
        const states = getStatesForForm(form);
        states.forEach((entry) => {
          if (!entry.form) entry.form = form;
          placeSubmitInputFirst(form, entry.submitInput);
          if (entry.manualFallback) {
            entry.isValidSelection = false;
          } else {
            entry.isValidSelection = normalizeCommittedCoordinates(entry);
          }
        });
        const firstInvalid = states.find((entry) => entry.manualFallback || !entry.isValidSelection);
        if (!firstInvalid) return;

        states.forEach((entry) => {
          if (entry.isValidSelection && !entry.manualFallback) return;
          if (entry.manualFallback) {
            setError(
              entry,
              'Coordinates are required. Location autocomplete is currently unavailable, so this item cannot be added right now.',
              'fallback'
            );
            return;
          }
          setError(entry, 'Please select a location before adding to cart.', 'validation');
        });

        event.preventDefault();
        event.stopImmediatePropagation();
        firstInvalid.searchInput.focus();
      },
      true
    );
  }

  function enhanceTarget(target) {
    const sourceInput = findSourceInput(target);
    if (!sourceInput) return;
    if (sourceInput.dataset.kpoCoordinateEnhanced === 'true') return;
    sourceInput.dataset.kpoCoordinateEnhanced = 'true';

    const labelText = getLabelText(target, sourceInput);
    const ui = createPickerUi(labelText);
    const submitInput = document.createElement('input');
    submitInput.type = 'hidden';
    submitInput.name = buildSubmitInputName(sourceInput, labelText);
    submitInput.dataset.kpoCoordinateSubmit = 'true';
    const productForm = findProductForm(sourceInput, target);

    sourceInput.insertAdjacentElement('afterend', ui.root);
    if (productForm) {
      placeSubmitInputFirst(productForm, submitInput);
    } else {
      ui.root.insertAdjacentElement('afterend', submitInput);
    }

    const state = {
      target,
      root: ui.root,
      sourceInput,
      submitInput,
      searchInput: ui.searchInput,
      helperLine: ui.helperLine,
      addressLine: ui.addressLine,
      coordinatesLabel: ui.coordinatesLabel,
      coordinatesInput: ui.coordinatesInput,
      accuracyLine: ui.accuracyLine,
      errorLine: ui.errorLine,
      form: productForm,
      autocomplete: null,
      isValidSelection: false,
      lastCommittedQuery: '',
      manualFallback: false,
      errorType: '',
      blurValidationTimerId: null,
      originalReadOnly: sourceInput.readOnly,
      originalDisabled: sourceInput.disabled,
      originalTabIndex: sourceInput.getAttribute('tabindex'),
    };

    pickerStates.push(state);

    submitInput.disabled = false;
    sourceInput.disabled = true;
    sourceInput.readOnly = true;
    sourceInput.classList.add(SOURCE_HIDDEN_CLASS);
    sourceInput.setAttribute('tabindex', '-1');
    sourceInput.setAttribute('aria-hidden', 'true');

    const existingValue = sanitizeSingleLine((submitInput.value || sourceInput.value || '').trim());
    const existingDisplayValue = toDisplayDm(existingValue);
    if (existingDisplayValue) {
      state.isValidSelection = true;
      showCoordinates(state, existingDisplayValue);
      showAccuracy(state, 'Coordinates are rounded for perfect fit.');
      state.helperLine.textContent = 'Search again if you want to replace these saved coordinates.';
      setSourceValue(state, existingDisplayValue);
    } else if (existingValue) {
      setSourceValue(state, '');
    }

    bindSearchEvents(state);
    bindFormValidation(state);
    bindAutocomplete(state);
  }

  function refresh(root) {
    collectTargets(root).forEach((target) => enhanceTarget(target));
    pickerStates.forEach((state) => {
      if (!state || !state.sourceInput || !state.sourceInput.isConnected) return;
      if (!state.target || !isElementVisible(state.target)) {
        const hasValue = Boolean(sanitizeSingleLine(getCommittedCoordinateValue(state)));
        if (hasValue || state.isValidSelection || state.searchInput.value.trim()) {
          clearStateWithoutMessage(state);
        }
      }
    });
  }

  function scheduleRefresh() {
    if (scheduledRefresh) return;
    scheduledRefresh = true;
    window.requestAnimationFrame(() => {
      scheduledRefresh = false;
      refresh(document);
    });
  }

  function attachObserver() {
    if (mutationObserver || !document.body) return;

    mutationObserver = new MutationObserver(() => {
      scheduleRefresh();
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  function sanitizeSingleLine(value) {
    return String(value || '').replace(/[\r\n]+/g, ' ').trim();
  }

  function init() {
    refresh(document);
    attachObserver();
  }

  document.addEventListener('shopify:section:load', (event) => {
    refresh(event.target || document);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
